import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus'
import { Flex, Select, type SelectProps } from '@mantine/core'
import { useDisclosure, useEventListener } from '@mantine/hooks'
import { useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreItemCategory } from '@flecto/types'

import { useModalCategoryUpdate } from '../../../flecto-modules-saas/src/categories'
import { Icon } from '../../src'
import { useSelectOptions } from './useSelectOptions'

type SelectCustomCategoriesProps = Omit<SelectProps, 'data'> & {
  data?: CoreItemCategory[]
  onCategoryCreated?: (category: CoreItemCategory) => void
}

export function SelectCustomCategories({
  data = [],
  onCategoryCreated,
  ...props
}: SelectCustomCategoriesProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_inventory' })
  const [searchValue, onSearchChange] = useState('')
  const { openModal } = useModalCategoryUpdate()
  const [dropdownOpened, { close: closeDropdown, open: openDropdown }] =
    useDisclosure()
  const selectRef = useEventListener('focusout', () => closeDropdown())
  const selectData = useSelectOptions({ items: data })

  const handleCategoryNotFound = () => {
    closeDropdown()
    openModal({
      category: { name: searchValue },
      onSuccess: (newCategory) => {
        if (onCategoryCreated) {
          onCategoryCreated(newCategory)
        }
      },
    })
  }

  return (
    <Select
      data={selectData}
      data-testid='select-custom-categories'
      dropdownOpened={dropdownOpened}
      label={t('general.field_category.title')}
      nothingFoundMessage={
        <Flex
          align='center'
          c='brand.7'
          gap='sm'
          onClick={() => handleCategoryNotFound()}
          pl='sm'
          style={{ cursor: 'pointer' }}
        >
          <Icon icon={faPlus} size='md' />
          {t('new_category', {
            replace: {
              category: `"${searchValue}"`,
            },
          })}
        </Flex>
      }
      onClick={openDropdown}
      onFocusCapture={openDropdown}
      onOptionSubmit={closeDropdown}
      onSearchChange={onSearchChange}
      placeholder={t('general.field_category.placeholder')}
      ref={selectRef}
      searchable
      searchValue={searchValue}
      withAsterisk
      {...props}
    />
  )
}
